import partyV2 from '../data/partyV2.json';
import { DHondt, getLastElection } from './calcV2';

type GraphPollData = {
    labels: string[],
    groups: string[],
    dataNow: number[],
    dataBefore?: number[],
    election: number[],
}
type GraphLineData = {
    labels: string[],
    parties: string[],
    groups: string[],
    dataItems: number[][],
}
export type DiffData = {
    diffValues?: number[],
    newValues: number[],
    oldValues?: number[],
    parties: string[],
    groups: string[],
    dateNew: Date,
    dateOld?: Date,
    textDiff?: string,
}

export const excludeCountries = ["United Kingdom", "Norway", "Switzerland", "Iceland"];
const partyCountries = partyV2.Countries.filter(x => !excludeCountries.includes(x.Country));

export const getShort = (countryStat: CountryStat): string | null | undefined => {
    return partyCountries.find(x => x.Country === countryStat.Country && x.Region === countryStat.Region)?.Short;
}
export const getShort2 = (countryRegion: string): string | null | undefined => {
    return partyCountries.find(x => (x.Country === countryRegion && x.DataType === 'country') || x.Region === countryRegion)?.Short;
}

export const getAllCountries = (): CountryData[] => {
    return partyCountries;
}

const getPartyData = (countryStat: CountryStat): PartyData[] => {
    const result = getCountryData(countryStat).PartyData;
    return (result.length > 0 || !countryStat.Parent) || countryStat.Region === null ? result : getCountryData(countryStat.Parent).PartyData;
}
export const getCountryData = (countryStat: CountryStat): CountryData => {
    return partyV2.Countries.find(x => x.Region === countryStat.Region && x.Country === countryStat.Country) as CountryData;
}
const isPartyUsed = (countryStat: CountryStat, partyName: string): boolean => {
    return getPartyData(countryStat).find(x => x.Party === partyName)?.Use ?? false;
}
export const getUsedParties = (countryStat: CountryStat): PartyData[] | undefined => {
    return getPartyData(countryStat)?.filter(x => x.Use);
}
export const getGroup = (countryStat: CountryStat, partyName: string): string => {
    return getPartyData(countryStat)?.find(x => x.Party === partyName)?.Group ?? 
        (countryStat.Parent ? (getPartyData(countryStat.Parent)?.find(x => x.Party === partyName)?.Group ?? 'NI') : 'NI');
}

export const getParties = (countryStat: CountryStat): string[] | undefined => {
    return countryStat.Region != null || countryStat.Parties.length > 0 
        ? countryStat.Parties.filter(partyName => isPartyUsed(countryStat, partyName))
        : countryStat.Parent?.Parties.filter(partyName => isPartyUsed(countryStat, partyName));
}
/*export const getGroups = (countryStat: CountryStat): string[] => {
    return countryStat.Parties.filter(partyName => isPartyUsed(countryStat, partyName)).map(x => getGroup(countryStat, x));
}*/
export const getGroups = (countryStat: CountryStat): string[] => {
    return getPartyData(countryStat).filter(x => x.Use).map(x => x.Group);
}

// Get list of indices that correspond to index of parties from left to the right
// Might be a problem there
export const getLeftRightIndexList = (countryStat: CountryStat): number[] => {
    const usedParties = getUsedParties(countryStat);
    const result = usedParties?.map(party => 
        countryStat.Parties.map((x, i) => x === party.Party ? i : -1).find(x => x >= 0) ?? -1).filter(x => x > -1) ?? [];
    //console.log('getLR', usedParties?.map(x => x.Party), result, result.filter(x => x !== undefined).map(x => countryStat.Parties[x ?? 0]));
    return result;
}

const getAt = <T>(items: T[], index: number): T | null => {
    return items.length > index ? items[index] : null;
}

const getElectionData = (countryStat: CountryStat, electionIndex: number, isEu: boolean = false, isSeat: boolean = false): ElectionData | null => {
    return isSeat
        ?
            (isEu 
            ? getAt(countryStat.EuElectionsSeats, electionIndex)
            : getAt(countryStat.ElectionsSeats, electionIndex))
        :
            (isEu 
            ? getAt(countryStat.EuElections, electionIndex)
            : getAt(countryStat.Elections, electionIndex));
}
/*const getElectionDataOrdered = (countryStat: CountryStat, electionIndex: number, isEu: boolean = false, isSeat: boolean = false): ElectionData => {
    const election = getElectionData(countryStat, electionIndex, isEu, isSeat);
    const indexList = getLeftRightIndexList(countryStat);
    return { ...election, Values: indexList.map(x => election?.Values[x] ?? 0) };
}*/
/*const getElectionDiff = (countryStat: CountryStat, electionIndexNew: number, electionIndexOld: number, isEu: boolean = false, isSeat: boolean = false): DiffData | null => {
    const electionNew = getElectionData(countryStat, electionIndexNew, isEu, isSeat);
    if (!electionNew) return null;

    const electionOld = getElectionData(countryStat, electionIndexOld, isEu, isSeat);
    const indexList = getLeftRightIndexList(countryStat);
    //console.log(indexList);
    const parties = getUsedParties(countryStat);
    //console.log('parties used / parties in country', parties, countryStat.Parties, parties !== undefined ? indexList.map(x => parties[x]) : null);

    const electionOrderedNew = indexList.map(x => electionNew.Values[x]);
    //console.log(electionOrderedNew);
    const electionOrderedOld = electionOld ? indexList.map(x => electionOld.Values[x]) : undefined;
    return parties ? 
        { 
            dateNew: new Date(electionNew.Date ?? ''), 
            dateOld: electionOld ? new Date(electionOld.Date ?? '') : undefined, 
            newValues: electionOrderedNew,
            oldValues: electionOrderedOld,
            diffValues: electionOrderedOld ? electionOrderedNew.map((x, i) => Math.round(10 * (electionOrderedNew[i] - electionOrderedOld[i]))/10) : undefined,
            parties: parties.map(x => x.Party),
            groups: parties.map(x => x.Group),
            textDiff: parties.map((x, i) => ({ party: x.Party, group: x.Group, value: electionOrderedNew[i] }))
                .sort((a, b) => b.value - a.value)
                .map(x => x.party + " (" + x.group + ")" + ": " + x.value + (isSeat ? "" : "%")).reduce((s, a) => s + "\n" + a, ""),
        } 
        : null;
}*/
export const combine = (values: number[], parties: string[], groups: string[]): PartyValue[] => {
    return values.map((x, i) => ({value: values[i], party: parties[i], group: groups[i] }));
}

export const getElectionDiff2 = (countryStat: CountryStat, electionIndexNew: number, electionIndexOld: number, isEu: boolean = false, isSeat: boolean = false): DiffData | null => {
    const electionNew = getElectionData(countryStat, electionIndexNew, isEu, isSeat);
    //console.log('a:', electionNew);
    if (!electionNew) return null;

    const electionOld = getElectionData(countryStat, electionIndexOld, isEu, isSeat);
    //console.log('b:', electionOld);
    const elNew = withParty(countryStat, electionNew);
    const elOld = electionOld ? withParty(countryStat, electionOld) : null;
    //console.log('stuff: ', electionNew, electionOld);

    const indexList = getLeftRightIndexList(countryStat);
    //console.log(indexList);
    const parties = getUsedParties(countryStat);
    if (!parties) return null;
    //console.log('parties used / parties in country', parties, countryStat.Parties, parties !== undefined ? indexList.map(x => parties[x]) : null);

    const electionOrderedNew = parties?.map(x => elNew.find(y => y.party === x.Party)).map(x => x?.value ?? 0);
    const electionOrderedOld = parties?.map(x => elOld?.find(y => y.party === x.Party)).map(x => x?.value ?? 0);
    return parties ? 
        { 
            dateNew: new Date(electionNew.Date ?? ''), 
            dateOld: electionOld ? new Date(electionOld.Date ?? '') : undefined, 
            newValues: electionOrderedNew,
            oldValues: electionOrderedOld,
            diffValues: electionOrderedOld ? electionOrderedNew.map((x, i) => Math.round(10 * (electionOrderedNew[i] - electionOrderedOld[i]))/10) : undefined,
            parties: parties.map(x => x.Party),
            groups: parties.map(x => x.Group),
            textDiff: parties.map((x, i) => ({ party: x.Party, group: x.Group, value: electionOrderedNew[i] }))
                .sort((a, b) => b.value - a.value)
                .map(x => x.party + " (" + x.group + ")" + ": " + x.value + (isSeat ? "" : "%")).reduce((s, a) => s + "\n" + a, ""),
        } 
        : null;
}

/*export const getPollGraphData = (countryStat: CountryStat, pollIndex: number, isEu: boolean = false): GraphPollData => {
    const electionIndex = 0;
    const partiesIndices = countryStat.Parties.map((partyName, i) => isPartyUsed(countryStat, partyName) ? i : null);
    const parties = countryStat.Parties.filter((x, i) => partiesIndices.includes(i));
    
    const indexOfParties = getPartyData(countryStat).filter(x => x.Use)
        .map(x => countryStat.Parties.map((y, i) => y === x.Party ? i : null).find(x => x !== null));
    console.log('my parties', indexOfParties);

    const groups = indexOfParties.map(y => parties.map(x => getGroup(countryStat, x))[y ?? 0]);
    const labels = indexOfParties.map((y, i) => parties.map(x => x + " (" + groups[i] + ")")[y ?? 0]);

    const dataNow = indexOfParties.map(x => countryStat.Polls[pollIndex].Values[x ?? 0]);
    const firm = countryStat.Polls[pollIndex].Firm;
    const dataBefore = indexOfParties.map(x => 
        countryStat.Polls.slice(pollIndex + 1).find(x => x.Firm == firm)?.Values[x ?? 0] ?? 0);
    const election = isEu 
        ? indexOfParties.map(x => countryStat.EuElections[electionIndex].Values[x ?? 0])
        : indexOfParties.map(x => countryStat.Elections[electionIndex].Values[x ?? 0]);
    return { labels, groups, dataNow, dataBefore, election };
}*/
export const getPollGraphData = (countryStat: CountryStat, pollIndex: number, isEu: boolean = false): GraphPollData => {
    const electionIndex = 0;
    const indexList = getLeftRightIndexList(countryStat);

    const poll = countryStat.Polls[pollIndex];
    const partyPoll = withPartyOnPollsNoFilter(countryStat, poll);

    //console.log(countryStat, poll, pollIndex);
    const dataNow = indexList.map(x => x < partyPoll.length ? partyPoll[x].value : 0);
    //console.log('dataNow', dataNow, poll);
    const groups = indexList.map(x => x < partyPoll.length ? partyPoll[x].group : "NI");
    const labels = indexList.map((x, i) => x < partyPoll.length ? partyPoll[x].party + " (" + groups[i] + ")" : "");

    const pollBefore = countryStat.Polls.slice(pollIndex + 1).find(x => x.Firm === poll.Firm);
    const partyPollBefore = pollBefore ? withPartyOnPollsNoFilter(countryStat, pollBefore) : null;
    const dataBefore = partyPollBefore ? indexList.map(x => x < partyPoll.length ? (partyPollBefore[x]?.value ?? 0) : 0) : undefined;

    const election = indexList.map(x => x < countryStat.Elections[electionIndex].Values.length ? countryStat.Elections[electionIndex].Values[x] : 0);
    //console.log('election', labels, indexList, election);
    return { labels, groups, dataNow, dataBefore, election };
}

export const getPrediction = (countryStat: CountryStat): PartyValue[] | undefined => {
    const firms: { [key: string]: number } = {};
    const dayDiff = 10 * 24 * 60 * 60 * 1000;
    let maxCoef = 8;
    let count = 0;
    let lastDate = new Date(new Date().getTime() - dayDiff);
    const result = getUsedParties(countryStat)?.map(x => 
        ({ party: x.Party, group: x.Group, value: 0, index: countryStat.Parties.indexOf(x.Party) }) as PartyValue & { index: number });
    for (let i = 0; i < countryStat.Polls.length; ++i) {
        const poll = countryStat.Polls[i];
        let foundValue = firms[poll.Firm ?? ''];
        if (maxCoef === 0){ break; }
        const coef = Math.min(maxCoef, foundValue ? foundValue : maxCoef);
        if (coef === 0){ continue; }
        if (foundValue) {
            foundValue -= 2;
        } else {
            firms[poll.Firm ?? ''] = maxCoef;
        }

        const date = new Date(poll.DateTo);
        if (date <= lastDate) {
            maxCoef -= 2;
            lastDate = new Date(date.getTime() - dayDiff);
        }
        
        if (result) {
            for (const item of result) {
                item.value += coef * (poll.Values[item.index] ?? 0);
            }
            count += coef;
        }
    }
    return result?.map(x => ({ group: x.group, party: x.party, value: Math.round(10 * x.value / count)/10 }));
}
export const getPredictionSeats = (countryStat: CountryStat, prediction: PartyValue[]): PartyValue[] => {
    const countryData = getCountryData(countryStat);
    const values = DHondt(prediction.map(x => x.value), countryData.ParliamentSeats ?? 0, countryData.ParliamentThreshold ?? 0);
    const result = ([ ...prediction.map((x, i) => ({ ...x, value: values[i] })) ]);
    return result;
}

const getIndexOfParty = (countryStat: CountryStat, partyName: string): number => {
    return countryStat.Parties.indexOf(partyName);
}

export const getChangeSinceLastElection = (countryStat: CountryStat, prediction: PartyValue[]): PartyValue[] => {
    const lastElection = getLastElection(countryStat);
    return prediction.map(x => { 
        const lastElectionValue = lastElection.Values[getIndexOfParty(countryStat, x.party)];
        return ({ ...x, value: lastElectionValue ? Math.round(10 * (x.value - lastElectionValue))/10 : 0 });
    });
}

export const distinct = <T>(items: T[]) => {
    const result = [] as T[];
    for (var item of items) {
        if (!result.includes(item)) {
            result.push(item);
        }
    }
    return result;
}

export const getLineGraphData = (countryStat: CountryStat): GraphLineData => {
    const indexList = getLeftRightIndexList(countryStat);
    const parties = getUsedParties(countryStat)?.map(x => x.Party) ?? [];
    const groups = getGroups(countryStat);

    const polls = getMontlyPollData(countryStat);
    const labels = distinct(polls.map(x => dateString(firstDay(new Date(x.DateTo))))).reverse();
    /*const dataItems = parties.map((x, j) => 
        polls.map(poll => poll.Values.filter((x, i) => partiesIndices.includes(i))[j])
            .reverse());//*/
    //console.log(indexList, countryStat.Parties);
    //console.log(parties.map((x, i) => x + " - " + countryStat.Parties[i] + ': ' + polls.map(poll => poll.Values[indexList[i]])[0]));

    const dataItemsTemp = parties.map((x, j) => 
        polls.map(poll => ({ date: dateString(firstDay(new Date(poll.DateTo))), value: poll.Values[indexList[j]] }))
            .reverse());
    const dataItems2 = dataItemsTemp.map(x => labels.map(label => { 
        const temp = x.reduce((s, a) => ({ v: (label === a.date ? a.value : 0) + s.v, c: s.c + (label === a.date ? 1 : 0) }), { v: 0, c: 0 });
        return temp.v / temp.c;
    }));
    //console.log('dataItems: ', dataItems2, parties);
    return { labels, parties, groups, dataItems: dataItems2 };
}
const getMontlyPollData = (countryStat: CountryStat): Poll[] => {
    const pollsDate = countryStat.Polls.map(x => ({ ...x, date: new Date(x.DateTo) }) );
    const months = [...new Set(pollsDate.map(x => x.Year + '|' + x.date.getMonth()))];
    return months.map(x => {
        const filtered = pollsDate.filter(y => y.Year + '|' + y.date.getMonth() === x);
        const values = filtered.map(y => y.Values).reduce((a, s) => add(a, s), [0]).map(y => Math.round(10 * y / filtered.length)/10);
        return { ...filtered[0], Values: values };
    });
}
const add = (a: number[], b: number[]): number[] => {
    const res = [] as number[];
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; ++i) {
        if (i < a.length && i < b.length) {
            res.push(a[i] + b[i]);
        } else if (i < a.length) {
            res.push(a[i]);
        } else {
            res.push(b[i]);
        }
    }
    return res;
}
const firstDay = (date: Date): Date => {
    return new Date(date.setDate(1));
}

export const getPrevPoll = (countryStat: CountryStat, firm: string, pollIndex: number): Poll | undefined => {
    return countryStat.Polls.slice(pollIndex + 1).find(y => y.Firm === firm);
}

export const getPollDiff = (curr: IValues, prev: IValues): IValues => {
    return { ...curr, Values: curr.Values.map((x, i) => Math.round(10*(curr.Values[i] - prev.Values[i]))/10) };
}

export const getTitle = (countryRegion: string, poll: Poll) => countryRegion + " / " + poll.Firm + " / " + poll.Field;

export const dateString = (date: Date) => {
    return date.toLocaleDateString('de-DE', { weekday: undefined });
}

export const withParty = (countryStat: CountryStat, electionData: ElectionData): PartyValue[] => {
    return electionData.Values.map((x, i) => {
        const party = countryStat.Parties[i];
        return ({ value: x, party, group: getGroup(countryStat, party) }); }).filter(x => x.value > 0);
}
export const withPartyOnPolls = (countryStat: CountryStat, pollData: Poll): PartyValue[] => {
    return withPartyOnPollsNoFilter(countryStat, pollData).filter(x => x.value > 0);
}
export const withPartyOnPollsNoFilter = (countryStat: CountryStat, pollData: Poll): PartyValue[] => {
    return pollData.Values.map((x, i) => { 
        const party = countryStat.Parties[i];
        return ({ value: x, party, group: getGroup(countryStat, party) }); });
}

export const getParliamentSize = (countryStat: CountryStat): number | null => {
    return getCountryData(countryStat).ParliamentSeats;
}

export const getIndexOfMax = (arr: number[]) => {
    return arr.indexOf(Math.max(...arr));
}

export const getAllCombinationsOfCount = (size: number, ofCount: number): number[][] => {
    const res = [] as number[][];
    const length = Math.pow(2, size);
    for (var i = 0; i < length; ++i) {
        const curr = [] as number[];
        for (var j = 0; j < size; ++j) {
            if (i & Math.pow(2, j)){
                curr.push(j);
            }
        }
        if (curr.length === ofCount) {
            res.push(curr);
        }
    }
    return res;
}
